import { Autocomplete, Grid } from '@mui/material'
import OWInput from 'components/OWForm/OWInput'
import { forwardRef, useImperativeHandle, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
    ICreateCompanyAddress,
    useCreateCompanyContext,
} from '../CreateCompanyContext'
import useGetProvince from '../hooks/useGetProvince'
import { getPostalCode, getRegionLabel } from '../utils/convertCountryLabel'
import WhiteTextField from './WhiteTextField'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

export type TAddressDetailRef = {
    submitForm: () => Promise<ICreateCompanyAddress>
    resetForm: () => void
}

const validate = yup.object().shape({
    countryID: yup.number().required().min(1, 'please select country *'),
    postalCode: yup.string().required(),
    addressLine1: yup.string().required(),
    province: yup.string().required(),
})

type Props = {
    defaultAddress?: ICreateCompanyAddress
}
const AddressDetail = forwardRef<TAddressDetailRef, Props>(
    ({ defaultAddress }, ref) => {
        const { countries } = useCreateCompanyContext()

        const provinceRec = useGetProvince()

        const form = useForm<ICreateCompanyAddress>({
            defaultValues: defaultAddress || {
                province: '',
            },
            resolver: yupResolver(validate),
        })

        useImperativeHandle(ref, () => ({
            submitForm() {
                return new Promise((resolve, reject) => {
                    form.handleSubmit(resolve, reject)()
                })
            },
            resetForm() {
                form.reset()
            },
        }))

        const { control, watch, resetField } = form
        const countryID = watch('countryID')

        const regionLabel = useMemo(() => getRegionLabel(countryID), [
            countryID,
        ])
        const postalCode = useMemo(() => getPostalCode(countryID), [countryID])

        const provinces = countryID ? provinceRec[countryID] : []
        return (
            <>
                <Grid item xs={12}>
                    <Controller
                        control={control}
                        name="countryID"
                        render={({ field: { ref, ...field }, fieldState }) => {
                            return (
                                <Autocomplete
                                    fullWidth
                                    color="primary"
                                    options={countries.map((e) => e.countryID)}
                                    value={field.value}
                                    onChange={(e, option) => {
                                        field.onChange(option ?? 0)
                                        resetField('province', {
                                            defaultValue: '',
                                        })
                                    }}
                                    getOptionLabel={(option) => {
                                        return (
                                            countries.find(
                                                (e) => e.countryID === option,
                                            )?.name ?? ''
                                        )
                                    }}
                                    renderInput={(params) => {
                                        return (
                                            <WhiteTextField
                                                {...params}
                                                inputRef={ref}
                                                label="Country *"
                                                error={Boolean(
                                                    fieldState.error,
                                                )}
                                                helperText={
                                                    fieldState.error?.message
                                                }
                                            />
                                        )
                                    }}
                                />
                            )
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <OWInput
                        component={WhiteTextField}
                        name="addressLine1"
                        label="Address *"
                        control={control}
                        autoComplete="address-line1"
                        fullWidth
                        multiline
                        rows={4}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Controller
                        control={control}
                        name="province"
                        render={({ field: { ref, ...field }, fieldState }) => {
                            return (
                                <Autocomplete
                                    fullWidth
                                    color="primary"
                                    options={provinces.map((e) => e[0])}
                                    value={field.value}
                                    onChange={(e, option) => {
                                        field.onChange(option)
                                    }}
                                    renderInput={(params) => {
                                        return (
                                            <WhiteTextField
                                                {...params}
                                                inputRef={ref}
                                                label={`${regionLabel} *`}
                                                error={Boolean(
                                                    fieldState.error,
                                                )}
                                                helperText={
                                                    fieldState.error?.message
                                                }
                                            />
                                        )
                                    }}
                                />
                            )
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <OWInput
                        component={WhiteTextField}
                        name="postalCode"
                        label={postalCode}
                        control={control}
                        fullWidth
                        autoComplete="postal-code"
                    />
                </Grid>
            </>
        )
    },
)

export default AddressDetail
